import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { AccountSelectorComponent } from '../../../../../../_shared/components/account-selector/account-selector.component';
import { AssetAssignmentFilterComponent } from '../../../../../../_shared/components/asset-assignment-filter/asset-assignment-filter.component';
import { FormErrorMessageComponent } from '../../../../../../_shared/components/form-error-message/form-error-message.component';
import { buildAssetTree } from '../../../../../../_shared/helpers/hierarchy.helper';
import { Account, DriverAssignmentFilter } from '../../../../../../_shared/models';
import { UnifiedTreeItem } from '../../../../../../_shared/models/driver.model';
import { AccountService } from '../../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { IntervalTypeOption, ServiceIntervalGroup, ServiceTypeOption } from '../../../models/service-library.models';
import { ServiceLibraryService } from '../../../services/service-library.service';
import { SelectServicePartsModalComponent } from '../select-service-parts-modal/select-service-parts-modal.component';
import { ServiceTypes } from '../../../../../_shared/enums/service-type.enum';
import { LookupService } from '../../../../../_shared/services/lookup.service';
import { LookupPartType } from '../../../../../_shared/models/lookup.model';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-upsert-service-schedule-modal',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, FormsModule, AccountSelectorComponent, AssetAssignmentFilterComponent, FormErrorMessageComponent, NgbDropdownModule, TranslocoPipe],
  templateUrl: './upsert-service-schedule-modal.component.html',
  styleUrl: './upsert-service-schedule-modal.component.scss'
})
export class UpsertServiceScheduleModalComponent implements OnInit, OnDestroy {
  private readonly transloco: TranslocoService = inject(TranslocoService);
  private fb: FormBuilder = inject(FormBuilder);
  private modalInstance: NgbActiveModal = inject(NgbActiveModal);
  private modalService: NgbModal = inject(NgbModal);
  private toastr: ToastrService = inject(ToastrService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private serviceLibraryService: ServiceLibraryService = inject(ServiceLibraryService);
  private accountService: AccountService = inject(AccountService);
  private lookupService = inject(LookupService);

  private cancelSubject: Subject<void> = new Subject<void>();

  scheduleForm: FormGroup;
  selectedAccount: Account;
  submitted: boolean = false;
  isLoading: boolean = false;
  partTypes: LookupPartType[] = [];
  isPartTypesLoading = false;
  customNameError = '';
  scheduleId: number;
  isEditMode: boolean = false;
  isInitializing: boolean = false;

  assetsTree: UnifiedTreeItem[] = [];
  assetsLoaded: WritableSignal<boolean> = signal(true);
  assetFilter: DriverAssignmentFilter = { drivers: [], assets: [] };

  serviceTypes: ServiceTypeOption[] = [
    { id: ServiceTypes.Minor, name: 'Minor' },
    { id: ServiceTypes.Intermediate, name: 'Intermediate' },
    { id: ServiceTypes.Major, name: 'Major' },
  ];

  intervalTypes: IntervalTypeOption[] = [
    { id: 1, name: 'Distance', suffix: 'km' },
    { id: 2, name: 'Months', suffix: 'months' },
  ];

  serviceIntervalGroups: ServiceIntervalGroup[] = [];

  get serviceTypePlaceholder(): string { return this.transloco.translate('serviceLibrary.upsert.selectServiceType'); }
  get intervalTypePlaceholder(): string { return this.transloco.translate('serviceLibrary.upsert.selectIntervalType'); }

  get modalTitle(): string {
    return this.transloco.translate(this.isEditMode ? 'serviceLibrary.upsert.editTitle' : 'serviceLibrary.upsert.createTitle');
  }

  get serviceIntervalGroupsFormArray(): FormArray {
    return this.scheduleForm.get('serviceIntervalGroups') as FormArray;
  }

  preSelectedAccountId: number | null = null;
  preSelectedAssetIds: number[] = [];

  async ngOnInit(): Promise<void> {
    this.initForm();
    this.scheduleForm.disable();

    if (this.isEditMode) {
      this.isInitializing = true;
      await this.getPartTypes();
      await this.getScheduleByIdAsync();
      this.isInitializing = false;
    } else {
      this.getPartTypes();
      this.addServiceIntervalGroup();
    }
  }

  private initForm(): void {
    this.scheduleForm = this.fb.group({
      name: ['', Validators.required],
      serviceIntervalGroups: this.fb.array([]),
    });
  }

  private createServiceGroupForm(): FormGroup {
    return this.fb.group({
      serviceType: ['', Validators.required],
      intervalType: ['', Validators.required],
      interval: ['', Validators.required],
      estDownTime: [''],
    });
  }

  async getPartTypes() {
    try {
      this.isPartTypesLoading = true;
      const response = await this.lookupService.getPartTypesAsync();
      this.partTypes = response.data ?? [];

    } catch (error) {
      this.errorHandlerService.handleError(error);
    } finally {
      this.isPartTypesLoading = false;
    }
  }

  addServiceIntervalGroup(): void {
    this.serviceIntervalGroupsFormArray.push(this.createServiceGroupForm());
    this.serviceIntervalGroups.push({
      selectedPartTypes: [],
      enableEmailAlerts: false,
    });
  }

  removeServiceGroup(index: number): void {
    if (this.serviceIntervalGroupsFormArray.length <= 1) return;
    this.serviceIntervalGroupsFormArray.removeAt(index);
    this.serviceIntervalGroups.splice(index, 1);
  }

  getGroupForm(index: number): FormGroup {
    return this.serviceIntervalGroupsFormArray.at(index) as FormGroup;
  }

  onSelectAccount(account: Account): void {
    if (this.selectedAccount?.id === account?.id) return;

    this.selectedAccount = account;

    if (this.selectedAccount) {
      this.scheduleForm.enable();
      this.loadAssetsForAccount();
    }
  }

  private async loadAssetsForAccount(): Promise<void> {
    this.assetsLoaded.set(false);
    try {
      const accountIds = this.getAccountIdsWithDescendants(this.selectedAccount.id);
      const response = await this.accountService.getUserAccountAssets(accountIds, this.cancelSubject);
      if (response.data) {
        this.assetsTree = buildAssetTree(response.data);
      }
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
    } finally {
      this.assetsLoaded.set(true);
    }
  }

  private getAccountIdsWithDescendants(accountId: number): number[] {
    const allAccounts = this.accountService.getAccounts();
    const ids: number[] = [accountId];
    const collectChildren = (parentId: number) => {
      for (const account of allAccounts) {
        if (account.parentId === parentId) {
          ids.push(account.id);
          collectChildren(account.id);
        }
      }
    };
    collectChildren(accountId);
    return ids;
  }

  onAssetFilterChanged(filter: DriverAssignmentFilter): void {
    this.assetFilter = filter;
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  getAvailableServiceTypes(currentIndex: number): ServiceTypeOption[] {
    const selectedIds = this.serviceIntervalGroups
      .filter((_, idx) => idx !== currentIndex)
      .map(g => g.selectedServiceType?.id)
      .filter((id): id is ServiceTypes => id !== undefined);

    return this.serviceTypes.filter(t => !selectedIds.includes(t.id));
  }

  onServiceTypeSelect(index: number, serviceType: ServiceTypeOption): void {
    this.serviceIntervalGroups[index].selectedServiceType = serviceType;
    const control = this.getGroupForm(index).get('serviceType');
    control?.setValue(serviceType);
    control?.markAsTouched();
    control?.updateValueAndValidity();
  }

  getSelectedServiceTypeLabel(index: number): string {
    const selected = this.serviceIntervalGroups[index]?.selectedServiceType;
    return selected ? this.serviceTypeLabel(selected) : this.serviceTypePlaceholder;
  }

  serviceTypeLabel(type: ServiceTypeOption): string {
    return this.transloco.translate('serviceLibrary.serviceTypes.' + type.name.toLowerCase());
  }

  intervalTypeLabel(type: IntervalTypeOption): string {
    return this.transloco.translate(type.id === 1 ? 'serviceLibrary.upsert.distance' : 'serviceLibrary.upsert.months');
  }

  onIntervalTypeSelect(index: number, intervalType: IntervalTypeOption): void {
    this.serviceIntervalGroups[index].selectedIntervalType = intervalType;
    const control = this.getGroupForm(index).get('intervalType');
    control?.setValue(intervalType);
    control?.markAsTouched();
    control?.updateValueAndValidity();
  }

  getSelectedIntervalTypeLabel(index: number): string {
    const selected = this.serviceIntervalGroups[index]?.selectedIntervalType;
    return selected ? this.intervalTypeLabel(selected) : this.intervalTypePlaceholder;
  }

  getIntervalSuffix(index: number): string {
    const selected = this.serviceIntervalGroups[index]?.selectedIntervalType;
    return selected?.id === 2 ? this.transloco.translate('serviceLibrary.upsert.monthsSuffix') : selected?.suffix || '';
  }

  onToggleEmailAlerts(index: number): void {
    this.serviceIntervalGroups[index].enableEmailAlerts = !this.serviceIntervalGroups[index].enableEmailAlerts;
  }

  openSelectPartsModal(index: number): void {
    const group = this.serviceIntervalGroups[index];

    const modalRef = this.modalService.open(SelectServicePartsModalComponent, {
      centered: true,
      windowClass: 'select-service-parts-modal-window'
    });

    modalRef.componentInstance.partTypes = this.partTypes;
    modalRef.componentInstance.selectedPartIds = group.selectedPartTypes.map(p => p.id);

    modalRef.result.then((selectedPartTypes: LookupPartType[]) => {
      this.serviceIntervalGroups[index].selectedPartTypes = selectedPartTypes;
    }).catch(() => {});
  }

  removePart(groupIndex: number, partTypeId: number, event: MouseEvent): void {
    event.stopPropagation();
    this.serviceIntervalGroups[groupIndex].selectedPartTypes = this.serviceIntervalGroups[groupIndex].selectedPartTypes.filter(p => p.id !== partTypeId);
  }

  async getScheduleByIdAsync(): Promise<void> {
    try {
      const response = await this.serviceLibraryService.getServiceScheduleById(this.scheduleId);
      const data = response.data;
      if (!data) return;

      // Set account via preSelectedAccountId input (triggers applyPreSelection in AccountSelectorComponent)
      // ownerAccountId is null for OEM schedules (read-only — cannot reach edit flow)
      this.preSelectedAccountId = data.ownerAccountId;

      // Set name
      this.scheduleForm.get('name')?.setValue(data.name);

      // Build interval groups from API data
      for (const interval of data.intervals) {
        this.serviceIntervalGroupsFormArray.push(this.createServiceGroupForm());

        const groupIndex = this.serviceIntervalGroups.length;
        const selectedServiceType = this.serviceTypes.find(t => t.id === interval.serviceTypeId);
        const selectedIntervalType = this.intervalTypes.find(t => t.id === interval.intervalTypeId);
        const selectedPartTypes = this.partTypes.filter(p => interval.partTypeIds.includes(p.id));

        this.serviceIntervalGroups.push({
          selectedServiceType,
          selectedIntervalType,
          selectedPartTypes,
          enableEmailAlerts: false,
        });

        const form = this.getGroupForm(groupIndex);
        form.get('serviceType')?.setValue(selectedServiceType);
        form.get('intervalType')?.setValue(selectedIntervalType);
        form.get('interval')?.setValue(interval.intervalValue);
        form.get('estDownTime')?.setValue(interval.estimatedDowntime);
      }

      this.preSelectedAssetIds = data.assetIds ?? [];
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  async onSubmit(): Promise<void> {
    this.submitted = true;
    this.isLoading = true;

    try {
      const serviceScheduleIntervals = this.serviceIntervalGroups.map((group, i) => {
        const form = this.getGroupForm(i);
        return {
          serviceTypeId: group.selectedServiceType?.id,
          intervalTypeId: group.selectedIntervalType?.id,
          intervalValue: Number(form.get('interval')?.value) || 0,
          partTypeIds: group.selectedPartTypes.map(p => p.id),
          estimatedDowntime: Number(form.get('estDownTime')?.value) || 0,
        };
      });

      const payload = {
        ...(this.isEditMode && { id: this.scheduleId }),
        name: this.scheduleForm.get('name')?.value || '',
        ownerAccountId: this.selectedAccount.id,
        serviceScheduleIntervals,
        assetIds: this.assetFilter.assets.map(a => a.id),
      };

      await this.serviceLibraryService.upsertServiceScheduleAsync(payload);
      this.toastr.success(this.transloco.translate('serviceLibrary.upsert.saved'));
      this.modalInstance.close(true);
    } catch (error: any) {
      if (error?.error?.errorCode === "service-schedules/duplicate-service-schedule") {
        this.errorHandlerService.handleError(error, {
          showToast: false,
          callback: (message) => {
            this.customNameError = message;
            this.scheduleForm.get('name')?.setErrors({ custom: true });
            this.scheduleForm.get('name')?.markAsTouched();
          }
        });
      } else {
        this.errorHandlerService.handleError(error);
      }
    } finally {
      this.isLoading = false;
    }
  }

  getErrorMessage(field: string): string | null {
    const control = this.scheduleForm.get(field);

    if (control?.hasError('required')) {
      return this.transloco.translate('suppliers.modal.errors.required');
    }

    if (field === 'name' && control?.hasError('custom')) {
      return this.customNameError;
    }

    return null;
  }

  clearCustomNameError(): void {
    this.customNameError = '';
    this.scheduleForm.get('name')?.updateValueAndValidity();
  }

  getGroupErrorMessage(index: number, field: string): string | null {
    const control = this.getGroupForm(index).get(field);

    if (control?.hasError('required')) {
      return this.transloco.translate('suppliers.modal.errors.required');
    }

    return null;
  }

  closeModal(): void {
    this.modalInstance.dismiss();
  }
}